import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { BranchAPI, extractError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export function BranchesPanel() {
    const { permissions, refresh } = useAuth();
    const canCreate = permissions.includes("branch:create");
    const canUpdate = permissions.includes("branch:update");
    const canDelete = permissions.includes("branch:delete");

    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            setList(await BranchAPI.list());
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);

    async function onDelete(id) {
        try {
            await BranchAPI.remove(id);
            toast.success("Branch deleted");
            await load();
            await refresh();
        } catch (e) {
            toast.error(extractError(e));
        }
    }

    return (
        <Card className="p-6" data-testid="branches-panel">
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <h2 className="font-display text-lg font-bold tracking-tight">Branches</h2>
                    <p className="text-xs text-zinc-500">Each branch has its own GSTIN, currency and invoice sequence.</p>
                </div>
                {canCreate && <BranchDialog onSaved={() => { load(); refresh(); }} mode="create" />}
            </div>

            <div className="rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">Code</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">Name</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">State</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">Currency</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">Phone</TableHead>
                            <TableHead className="text-right font-mono text-[10px] uppercase tracking-wider">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={6} className="py-6 text-center text-sm text-zinc-500">
                                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && list.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} className="py-6 text-center text-sm text-zinc-500">
                                    No branches yet.
                                </TableCell>
                            </TableRow>
                        )}
                        {list.map((b) => (
                            <TableRow key={b.id} data-testid={`branch-row-${b.code}`}>
                                <TableCell className="font-mono text-xs uppercase text-brand-redDeep dark:text-brand-red">
                                    {b.code}
                                </TableCell>
                                <TableCell className="font-medium">{b.name}</TableCell>
                                <TableCell className="font-mono text-xs uppercase">{b.state_code}</TableCell>
                                <TableCell className="font-mono text-xs">{b.currency}</TableCell>
                                <TableCell className="font-mono text-xs">{b.phone || "—"}</TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end gap-2">
                                        {canUpdate && (
                                            <BranchDialog
                                                mode="edit"
                                                branch={b}
                                                onSaved={() => { load(); refresh(); }}
                                            />
                                        )}
                                        {canDelete && (
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="text-rose-600"
                                                        data-testid={`branch-delete-${b.code}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Delete branch?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            This soft-deletes <span className="font-mono text-brand-redDeep">{b.code}</span>. Financial records stay
                                                            intact and audit trails are preserved.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction
                                                            data-testid={`branch-delete-confirm-${b.code}`}
                                                            className="bg-rose-600 text-white hover:bg-rose-700"
                                                            onClick={() => onDelete(b.id)}
                                                        >
                                                            Delete
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </Card>
    );
}

function BranchDialog({ mode, branch, onSaved }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState(() =>
        branch
            ? { ...branch }
            : {
                  code: "",
                  name: "",
                  state_code: "KA",
                  currency: "INR",
                  timezone: "Asia/Kolkata",
                  gstin: "",
                  address: "",
                  phone: "",
                  email: "",
              }
    );
    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function save() {
        setBusy(true);
        try {
            const body = { ...form };
            if (!body.gstin) delete body.gstin;
            if (!body.email) delete body.email;
            if (mode === "create") await BranchAPI.create(body);
            else await BranchAPI.update(branch.id, body);
            toast.success(mode === "create" ? "Branch created" : "Branch updated");
            setOpen(false);
            onSaved && onSaved();
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {mode === "create" ? (
                    <Button
                        className="bg-brand-red text-white hover:bg-brand-redDeep"
                        data-testid="branch-create-open"
                    >
                        <Plus className="mr-1 h-4 w-4" /> New branch
                    </Button>
                ) : (
                    <Button size="sm" variant="outline" data-testid={`branch-edit-${branch.code}`}>
                        Edit
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>{mode === "create" ? "Create branch" : `Edit ${branch.code}`}</DialogTitle>
                    <DialogDescription>
                        Each branch has its own invoice sequence and GST profile.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <F label="Code" required>
                        <Input
                            value={form.code}
                            onChange={(e) => set("code", e.target.value.toUpperCase())}
                            className="font-mono uppercase"
                            disabled={mode === "edit"}
                            data-testid="branch-code"
                        />
                    </F>
                    <F label="Name" required>
                        <Input
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            data-testid="branch-name"
                        />
                    </F>
                    <F label="State code">
                        <Input
                            value={form.state_code}
                            onChange={(e) => set("state_code", e.target.value.toUpperCase())}
                            className="font-mono uppercase"
                            data-testid="branch-state"
                        />
                    </F>
                    <F label="Currency">
                        <Input
                            value={form.currency}
                            onChange={(e) => set("currency", e.target.value.toUpperCase())}
                            className="font-mono uppercase"
                            data-testid="branch-currency"
                        />
                    </F>
                    <F label="GSTIN (optional)">
                        <Input
                            value={form.gstin || ""}
                            onChange={(e) => set("gstin", e.target.value.toUpperCase())}
                            className="font-mono uppercase"
                            placeholder="29ABCDE1234F1Z5"
                            data-testid="branch-gstin"
                        />
                    </F>
                    <F label="Phone">
                        <Input
                            value={form.phone || ""}
                            onChange={(e) => set("phone", e.target.value)}
                            data-testid="branch-phone"
                        />
                    </F>
                    <F label="Email">
                        <Input
                            type="email"
                            value={form.email || ""}
                            onChange={(e) => set("email", e.target.value)}
                            data-testid="branch-email"
                        />
                    </F>
                    <F label="Timezone">
                        <Input
                            value={form.timezone || ""}
                            onChange={(e) => set("timezone", e.target.value)}
                            data-testid="branch-tz"
                        />
                    </F>
                    <div className="md:col-span-2">
                        <F label="Address">
                            <Input
                                value={form.address || ""}
                                onChange={(e) => set("address", e.target.value)}
                                data-testid="branch-address"
                            />
                        </F>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        disabled={busy}
                        data-testid="branch-save"
                        className="bg-brand-red text-white hover:bg-brand-redDeep"
                    >
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function F({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {label}
                {required && <span className="ml-0.5 text-brand-red">*</span>}
            </Label>
            {children}
        </div>
    );
}
